import { onChildAdded, onChildChanged, onChildRemoved } from "firebase/database";
import {
  resolveRtdbRefPath,
  trackRtdbListenerClose,
  trackRtdbListenerOpen,
  trackRtdbLogicalSnapshot,
} from "./rtdbTrafficMonitor.js";

function defaultMapItem(id, value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return { id, ...value };
  }
  return { id, value };
}

export function listenRtdbListIncremental(queryRef, options = {}) {
  const mapItem = typeof options.mapItem === "function" ? options.mapItem : defaultMapItem;
  const sortItems = typeof options.sortItems === "function" ? options.sortItems : null;
  const isItemEqual =
    typeof options.isItemEqual === "function" ? options.isItemEqual : null;
  const onData = typeof options.onData === "function" ? options.onData : () => {};
  const onError = typeof options.onError === "function" ? options.onError : () => {};
  const onReady = typeof options.onReady === "function" ? options.onReady : null;
  const emitDebounceMs = Number.isFinite(options.emitDebounceMs)
    ? Math.max(0, options.emitDebounceMs)
    : 16;

  const byId = new Map();
  // IDs que já receberam um evento realtime durante a leitura inicial. A
  // resposta de `get()` não pode voltar a introduzir uma versão anterior.
  const realtimeTouchedIds = new Set();
  let stopped = false;
  let ready = false;
  let emitTimer = null;
  let readyTimer = null;
  const basePath = resolveRtdbRefPath(queryRef);
  const listenerToken = trackRtdbListenerOpen(basePath);
  let unsubAdded = null;
  let unsubChanged = null;
  let unsubRemoved = null;

  const emit = () => {
    if (stopped) return;
    let arr = Array.from(byId.values());
    if (sortItems) arr = sortItems(arr);
    onData(arr);
  };

  const scheduleEmit = () => {
    if (stopped) return;
    if (emitTimer) return;
    emitTimer = setTimeout(() => {
      emitTimer = null;
      emit();
    }, emitDebounceMs);
  };

  const scheduleReady = () => {
    if (stopped || ready || !onReady) return;
    if (readyTimer) clearTimeout(readyTimer);
    // `child_added` entrega primeiro os filhos já existentes e continua depois
    // apenas com alterações. Um pequeno debounce marca o fim desse lote sem
    // executar também um `get()` da coleção inteira (que duplicava todos os
    // bytes em cada abertura de painel).
    readyTimer = setTimeout(() => {
      readyTimer = null;
      if (stopped || ready) return;
      ready = true;
      onReady();
    }, 120);
  };

  const upsertFromSnapshot = (snap) => {
    if (!snap?.key) return;
    trackRtdbLogicalSnapshot({
      path: `${basePath}/${snap.key}`,
      payload: snap.val(),
      source: "incremental:child",
    });
    const nextItem = mapItem(snap.key, snap.val());
    const prevItem = byId.get(snap.key);
    if (isItemEqual && prevItem && isItemEqual(prevItem, nextItem)) {
      return false;
    }
    byId.set(snap.key, nextItem);
    return true;
  };

  const removeFromSnapshot = (snap) => {
    if (!snap?.key) return;
    byId.delete(snap.key);
  };

  const attachRealtimeListeners = () => {
    if (stopped) return;
    if (unsubAdded || unsubChanged || unsubRemoved) return;

    unsubAdded = onChildAdded(
      queryRef,
      (snap) => {
        if (snap?.key) realtimeTouchedIds.add(snap.key);
        if (upsertFromSnapshot(snap) !== false) {
          scheduleEmit();
        }
        scheduleReady();
      },
      (err) => onError(err)
    );

    unsubChanged = onChildChanged(
      queryRef,
      (snap) => {
        if (snap?.key) realtimeTouchedIds.add(snap.key);
        if (upsertFromSnapshot(snap) !== false) {
          scheduleEmit();
        }
      },
      (err) => onError(err)
    );

    unsubRemoved = onChildRemoved(
      queryRef,
      (snap) => {
        if (snap?.key) realtimeTouchedIds.add(snap.key);
        removeFromSnapshot(snap);
        scheduleEmit();
      },
      (err) => onError(err)
    );
  };

  // Liga primeiro os eventos incrementais. Se a leitura inicial e a ligação
  // dos listeners forem feitas pela ordem inversa, uma remoção ocorrida nesse
  // intervalo pode nunca chegar ao cliente e o item antigo volta à cache.
  attachRealtimeListeners();
  // Também conclui corretamente para listas vazias, nas quais não existe um
  // primeiro `child_added` que possa iniciar o debounce acima.
  scheduleReady();

  return () => {
    stopped = true;
    trackRtdbListenerClose(listenerToken);
    if (emitTimer) {
      clearTimeout(emitTimer);
      emitTimer = null;
    }
    if (readyTimer) {
      clearTimeout(readyTimer);
      readyTimer = null;
    }
    try {
      unsubAdded?.();
    } catch {}
    try {
      unsubChanged?.();
    } catch {}
    try {
      unsubRemoved?.();
    } catch {}
  };
}
